import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import AgentPicker from './AgentPicker';
import {
  Phone, Headphones, ClipboardList, Presentation, DollarSign, Users, Clock, Loader2, LayoutGrid,
  ArrowUp, ArrowDown,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, getDaysInMonth } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iso = (d) => format(d, 'yyyy-MM-dd');

const ROWS = [
  { key: 'dials_made',          label: 'Dials',         icon: Phone,         color: '#4318FF' },
  { key: 'contacts_made',       label: 'Contacts',      icon: Headphones,    color: '#D97706' },
  { key: 'appointments_set',    label: 'Appointments',  icon: ClipboardList, color: '#EA580C' },
  { key: 'presentations_given', label: 'Presentations', icon: Presentation,  color: '#DB2777' },
  { key: 'sales_made',          label: 'Sales',         icon: DollarSign,    color: '#059669' },
  { key: 'recruits_onboarded',  label: 'Recruiting',    icon: Users,         color: '#7C3AED' },
];

// Fixed weekly buckets anchored on the 1st of the month: 1-7, 8-14, 15-21, 22-28, 29-end.
const weekBuckets = (monthDate) => {
  const dim = getDaysInMonth(monthDate);
  const out = [];
  for (let s = 1; s <= dim; s += 7) {
    const e = Math.min(s + 6, dim);
    out.push({ start: s, end: e, label: `Week ${out.length + 1}`, sub: `${s}-${e}` });
  }
  return out;
};

export default function TrackerWeeklyGrid() {
  const { user, getAuthHeader } = useAuth();
  const now = new Date();
  const [monthIdx, setMonthIdx] = useState(now.getMonth()); // 0-11, current year only
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const monthDate = useMemo(() => new Date(now.getFullYear(), monthIdx, 1), [monthIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  const weeks = useMemo(() => weekBuckets(monthDate), [monthDate]);

  // Months of the current year, January through the current month.
  const monthOptions = useMemo(
    () => Array.from({ length: now.getMonth() + 1 }, (_, i) => ({
      idx: i,
      label: format(new Date(now.getFullYear(), i, 1), 'MMMM'),
    })),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Load viewable agents (self + downline; admin = everyone)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tracker/viewable-agents`, getAuthHeader());
        if (!cancelled && Array.isArray(res.data)) setAgents(res.data);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = { start_date: iso(startOfMonth(monthDate)), end_date: iso(endOfMonth(monthDate)) };
      if (selectedAgentId && selectedAgentId !== user?.id) params.user_id = selectedAgentId;
      const res = await axios.get(`${API}/kpi`, { ...getAuthHeader(), params });
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [monthDate, selectedAgentId, user?.id, getAuthHeader]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Aggregate per bucket: { [rowKey]: number[], hours: minutes[] }
  const byWeek = useMemo(() => {
    const sums = {};
    for (const r of ROWS) sums[r.key] = weeks.map(() => 0);
    sums.work_minutes = weeks.map(() => 0);
    for (const e of entries) {
      const day = parseInt((e.date || '').slice(8), 10);
      if (!day) continue;
      const wi = weeks.findIndex((w) => day >= w.start && day <= w.end);
      if (wi === -1) continue;
      for (const r of ROWS) sums[r.key][wi] += e[r.key] || 0;
      sums.work_minutes[wi] +=
        (e.dials_made || 0) * 1 + (e.presentations_given || 0) * 60 + (e.recruits_onboarded || 0) * 30;
    }
    return sums;
  }, [entries, weeks]);

  const bestHoursWeek = useMemo(() => {
    const arr = byWeek.work_minutes || [];
    const max = Math.max(...arr, 0);
    return max > 0 ? arr.indexOf(max) : -1;
  }, [byWeek]);

  const fmtHours = (mins) => (Math.round((mins / 60) * 10) / 10).toLocaleString(undefined, { maximumFractionDigits: 1 });

  // Week-over-week delta for any metric value vs the prior week.
  const wowDelta = (arr, i) => {
    if (i === 0) return null;
    const cur = arr?.[i] || 0;
    const prev = arr?.[i - 1] || 0;
    if (!prev && !cur) return null;
    if (!prev && cur > 0) return { kind: 'new' };
    const pct = Math.round(((cur - prev) / prev) * 100);
    if (pct === 0) return { kind: 'flat' };
    return { kind: pct > 0 ? 'up' : 'down', pct: Math.abs(pct) };
  };

  const DeltaBadge = ({ d, testId }) => {
    if (!d) return <span className="block h-[14px]" />;
    if (d.kind === 'up') return (
      <span data-testid={testId} className="inline-flex items-center gap-px text-[10px] font-bold text-emerald-600">
        <ArrowUp className="h-2.5 w-2.5" />{d.pct}%
      </span>
    );
    if (d.kind === 'down') return (
      <span data-testid={testId} className="inline-flex items-center gap-px text-[10px] font-bold text-rose-500">
        <ArrowDown className="h-2.5 w-2.5" />{d.pct}%
      </span>
    );
    if (d.kind === 'new') return (
      <span data-testid={testId} className="text-[10px] font-bold text-sky-600">New</span>
    );
    return <span data-testid={testId} className="text-[10px] font-bold text-[#C9D2E8]">0%</span>;
  };

  return (
    <Card className="bg-white" data-testid="tracker-weekly-grid">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#A3AED0]">
              <LayoutGrid className="h-4 w-4 text-[#4318FF]" /> Activity Grid 2
            </div>
            <div className="mt-0.5 text-[11px] text-[#A3AED0]">
              {format(monthDate, 'MMMM yyyy')} — week-over-week totals, weeks start on the 1st.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <AgentPicker
              agents={agents}
              selectedAgentId={selectedAgentId}
              onChange={setSelectedAgentId}
              testIdPrefix="weekly-grid"
            />
            <Select value={String(monthIdx)} onValueChange={(v) => setMonthIdx(parseInt(v, 10))}>
              <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="weekly-grid-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m.idx} value={String(m.idx)} data-testid={`weekly-grid-month-${m.idx}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#2447D6]" /></div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full min-w-[640px] border-separate border-spacing-y-1">
              <thead>
                <tr>
                  <th className="w-[150px] min-w-[150px] pr-2" />
                  {weeks.map((w) => (
                    <th key={w.label} className="px-1 text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-[#A3AED0]">{w.label}</div>
                      <div className="text-[11px] tabular-nums text-[#A3AED0]">{w.sub}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr key={r.key} data-testid={`weekly-grid-row-${r.key}`}>
                    <td className="pr-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="flex h-6 w-6 items-center justify-center rounded-md"
                          style={{ backgroundColor: `${r.color}14` }}
                        >
                          <r.icon className="h-3.5 w-3.5" style={{ color: r.color }} />
                        </span>
                        <span className="text-[13px] font-semibold text-[#2B3674]">{r.label}</span>
                      </div>
                    </td>
                    {weeks.map((w, i) => {
                      const v = byWeek[r.key]?.[i] || 0;
                      const d = wowDelta(byWeek[r.key], i);
                      return (
                        <td key={w.label} className="px-1 text-center align-top">
                          <div
                            data-testid={`weekly-grid-cell-${r.key}-w${i + 1}`}
                            className={`font-['Manrope'] text-sm font-extrabold tabular-nums ${
                              v > 0 ? 'text-[#2B3674]' : 'text-[#C9D2E8]'
                            }`}
                          >
                            {v.toLocaleString()}
                          </div>
                          <DeltaBadge d={d} testId={`weekly-grid-delta-${r.key}-w${i + 1}`} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Hours row — auto-calculated, highlighted */}
                <tr data-testid="weekly-grid-row-hours">
                  <td className="border-t border-[#E9EDF7] pt-2 pr-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#E0F2FE]">
                        <Clock className="h-3.5 w-3.5 text-[#0284C7]" />
                      </span>
                      <span className="text-[13px] font-semibold text-[#2B3674]">Hours</span>
                      <span
                        className="rounded bg-[#F4F7FE] px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[#A3AED0]"
                        title="Auto-calculated: 1 dial = 1 min, 1 presentation = 1 hr, 1 recruit = 30 min"
                      >
                        Auto
                      </span>
                    </div>
                  </td>
                  {weeks.map((w, i) => {
                    const mins = byWeek.work_minutes?.[i] || 0;
                    const isBest = i === bestHoursWeek;
                    const d = wowDelta(byWeek.work_minutes, i);
                    return (
                      <td key={w.label} className="border-t border-[#E9EDF7] px-1 pt-2 text-center align-top">
                        <div
                          data-testid={`weekly-grid-cell-hours-w${i + 1}`}
                          className={`mx-auto flex h-8 min-w-[64px] max-w-[110px] items-center justify-center rounded-lg font-['Manrope'] text-sm font-extrabold tabular-nums ${
                            isBest
                              ? 'bg-gradient-to-r from-[#0EA5E9] to-[#38BDF8] text-white shadow-[0_6px_16px_rgba(14,165,233,0.35)]'
                              : mins > 0
                                ? 'bg-[#F4F7FE] text-[#2B3674]'
                                : 'text-[#C9D2E8]'
                          }`}
                        >
                          {fmtHours(mins)}
                        </div>
                        <div className="mt-0.5">
                          <DeltaBadge d={d} testId={`weekly-grid-delta-hours-w${i + 1}`} />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
